import React from 'react';
import { useArtifacts } from './provider';
import { shouldHydrateCanvasDraftFromAuthoritative } from './draft-sync.js';

function documentSaveOptions(options) {
  const defaults = {
    activeTabKey: '', analysisPayload: null, analysisTabKey: 'analysis',
    authoritativeIntakeContent: '', canvasDraftEditing: false,
    currentDraftContent: '', requirementTabKey: 'requirement',
  };
  return { ...options, ...Object.fromEntries(Object.entries(defaults).map(([key, fallback]) => [
    key, options[key] === undefined ? fallback : options[key],
  ])) };
}

/** Keep editable input separate from immutable canonical content, including after CAS acceptance. */
export function useCanvasDocumentSaveState(options = {}) {
  const { activeTabKey, analysisPayload, analysisTabKey, authoritativeIntakeContent,
    canvasDraftEditing, currentDraftContent, onHydrateDraftEditorValue, requirementTabKey } = documentSaveOptions(options);
  const { lastHydratedIntakeContentRef, lastHydratedAnalysisContentRef } = useArtifacts();
  const canonicalIntakeContent = String(authoritativeIntakeContent ?? '');
  const canonicalAnalysisContent = String(analysisPayload?.markdown ?? '');
  const isRequirementDraftEditing = canvasDraftEditing && activeTabKey === requirementTabKey;
  const isAnalysisDraftEditing = canvasDraftEditing && activeTabKey === analysisTabKey;

  React.useEffect(() => {
    if (!shouldHydrateCanvasDraftFromAuthoritative({
      canvasDraftEditing: isRequirementDraftEditing, currentDraftContent,
      lastHydratedAuthoritativeContent: lastHydratedIntakeContentRef.current,
      nextAuthoritativeContent: canonicalIntakeContent,
    })) return;
    lastHydratedIntakeContentRef.current = canonicalIntakeContent;
    if (activeTabKey === requirementTabKey) onHydrateDraftEditorValue?.(canonicalIntakeContent);
  }, [activeTabKey, canonicalIntakeContent, currentDraftContent, isRequirementDraftEditing,
    onHydrateDraftEditorValue, requirementTabKey, lastHydratedIntakeContentRef]);

  React.useEffect(() => {
    if (!analysisPayload || !shouldHydrateCanvasDraftFromAuthoritative({
      canvasDraftEditing: isAnalysisDraftEditing, currentDraftContent,
      lastHydratedAuthoritativeContent: lastHydratedAnalysisContentRef.current,
      nextAuthoritativeContent: canonicalAnalysisContent,
    })) return;
    lastHydratedAnalysisContentRef.current = canonicalAnalysisContent;
    if (activeTabKey === analysisTabKey) onHydrateDraftEditorValue?.(canonicalAnalysisContent);
  }, [activeTabKey, analysisPayload, analysisTabKey, canonicalAnalysisContent, currentDraftContent,
    isAnalysisDraftEditing, onHydrateDraftEditorValue, lastHydratedAnalysisContentRef]);

  return { resolvedIntakeContent: canonicalIntakeContent, resolvedAnalysisContent: canonicalAnalysisContent,
    resolvedAnalysisPayload: analysisPayload };
}

export default useCanvasDocumentSaveState;
